import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { ThemeToggle } from "../ui";
import { monogramOf, useTenantBranding, type TenantBrandingState } from "../auth/tenantBranding";
import { resolveSignInTexts, useSignInTexts, type SignInTextsState } from "../auth/signInTexts";

// Shared shell for the public auth surfaces — login, email-link request/redeem,
// OIDC callback (E1 US-1.1 / US-1.3). Rendered OUTSIDE the app shell.
//
// Two panes (Brändiehdotus v2, ref. Copy.ai sign-in): a coral→indigo Verso brand
// aside on wide screens beside the sign-in card. The card carries the tenant
// branding slot — the tenant's own LOGO and NAME, read from the unauthenticated
// `GET /api/auth/tenant` lookup the `:slug` addresses (US-1.1), not derived from
// the URL: a slug is an address, not a brand, and a monogram cut from it told the
// learner nothing about whose organisation they were signing in to. Plus the fi/en
// language toggle and the light/dark theme control. Every colour is a Verso token,
// so both themes come for free. Page-specific content is `children`.
//
// The aside's TWO TEXTS are the platform operator's since the owner's decision B1 (4.11.2026): a
// PLATFORM_ADMIN sets them for the whole platform, fi and en, from the operator console's
// `/sign-in-texts` page, and an empty one falls back to this product's own `auth.tagline` /
// `auth.taglineSub`. They are read once per document through the anonymous `GET /api/auth/sign-in-texts`
// (../auth/signInTexts) — see `BrandTagline` below for what is drawn while that read is in flight, and
// why it is not the default.
export function AuthLayout({
  children,
  platformName,
}: {
  children: ReactNode;
  /**
   * The name shown in the branding slot when no tenant branding is available — the platform's own
   * identity. Defaults to the product's name; the operator console (apps/platform, ADR-0014 §6 phase 2)
   * passes its own, because its host addresses no tenant at all and "Verso People" would name the
   * product a platform operator is not signing in to.
   */
  platformName?: string;
}) {
  const { t, i18n } = useTranslation();
  const tenant = useTenantBranding();
  const signInTexts = useSignInTexts();

  return (
    <main id="main" className="auth">
      {/* Decorative Verso platform brand — hidden on narrow screens. */}
      <aside className="auth__aside" aria-hidden="true">
        <div className="auth__aside-inner">
          <div className="auth__wordmark">
            <span className="auth__wordmark-mark">V</span>
            <span>Verso</span>
          </div>
          <BrandTagline
            state={signInTexts}
            language={i18n.language}
            defaults={{ tagline: t("auth.tagline"), taglineSub: t("auth.taglineSub") }}
          />
        </div>
      </aside>

      <div className="auth__panel">
        <div className="auth__card">
          <TenantBrand tenant={tenant} platformName={platformName ?? t("app.title")} />

          {children}

          <div className="auth__footer">
            <LanguageSwitcher />
            <ThemeToggle />
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * The brand panel's two texts — the platform operator's, when they have set them (owner's decision B1,
 * 4.11.2026), otherwise the ones this product ships in the translation file.
 *
 * SAME PRINCIPLE AS `TenantBrand` BELOW, and for the same reason: while the read is in flight the panel
 * draws a PLACEHOLDER of the same size rather than the shipped default, so a visitor never reads one
 * slogan and then watches it turn into another. The aside is `aria-hidden`, so the placeholder is a
 * purely visual matter — nothing is announced either way.
 *
 * Which text wins is not decided here: `resolveSignInTexts` is a pure function and owns the whole table
 * (language, nulls, blanks, and a read that failed), so the rules have a test that needs no browser.
 */
function BrandTagline({
  state,
  language,
  defaults,
}: {
  state: SignInTextsState;
  language: string | undefined;
  defaults: { tagline: string; taglineSub: string };
}) {
  const resolved = resolveSignInTexts(state, language, defaults);

  if (resolved.status === "pending") {
    return (
      <div className="auth__tagline-pending" data-testid="auth-tagline-pending">
        <span className="auth__tagline auth__tagline--pending" />
        <span className="auth__aside-foot auth__aside-foot--pending" />
      </div>
    );
  }

  return (
    <>
      <p className="auth__tagline" data-testid="auth-tagline">
        {resolved.tagline}
      </p>
      <p className="auth__aside-foot" data-testid="auth-tagline-sub">
        {resolved.taglineSub}
      </p>
    </>
  );
}

/**
 * The branding slot. While the lookup is in flight it is a placeholder rather than a guess, so the name
 * never flips from something wrong to something right in front of the learner. A tenant that is unknown
 * (or unreachable) falls back to the platform's own identity: this slot must not assert an organisation
 * the backend did not confirm — the page's own 404 state is what names the problem.
 */
function TenantBrand({
  tenant,
  platformName,
}: {
  tenant: TenantBrandingState;
  platformName: string;
}) {
  if (tenant.status === "loading") {
    return (
      <div className="auth__brand" data-testid="auth-brand" aria-busy="true">
        <span className="auth__brand-mark auth__brand-mark--pending" aria-hidden="true" />
        <span className="auth__brand-name auth__brand-name--pending" aria-hidden="true" />
      </div>
    );
  }

  const branding = tenant.status === "ready" ? tenant.branding : null;
  const name = branding?.name ?? platformName;

  return (
    <div className="auth__brand" data-testid="auth-brand">
      {branding?.logoUrl ? (
        // alt="" on purpose: the name sits right beside it, so an alt would be read twice.
        <img
          className="auth__brand-logo"
          data-testid="auth-brand-logo"
          src={branding.logoUrl}
          alt=""
        />
      ) : (
        <span className="auth__brand-mark" data-testid="auth-brand-mark" aria-hidden="true">
          {monogramOf(name)}
        </span>
      )}
      <span className="auth__brand-name">{name}</span>
    </div>
  );
}
